/**
 * @fileoverview
 * This file contains all the functions that are used to retrieve data from the
 * Embed feed API.
 */

import type { AxiosInstance } from 'axios'

import type { IAPI_Channel } from '@core/api/types/api_channel'
import type { Api_Feed, FeedApiParams } from '@core/api/types/api_feed'
import { preventCache } from '@core/api/utils/preventCache'
import type { Channel } from '@core/channel'

/**
 * Retrieves a channel by username
 * @param api
 * @returns
 */
export async function getChannel(
  api: AxiosInstance,
  apiCacheNonce: string,
  username: string,
): Promise<IAPI_Channel> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channels/${encodeURIComponent(username)}`,
  )
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get(...preventCache(url.href))
  return response.data
}

/**
 * Retrieves a channel by id
 * @param api
 * @returns
 */
export async function getChannelById(
  api: AxiosInstance,
  encodedId: string,
): Promise<Channel> {
  const response = await api.get(
    ...preventCache(`/api/channels/by_id/${encodeURIComponent(encodedId)}`),
  )
  return response.data
}

/**
 *
 * @param username
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedUserTimelineFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { username, pageSize, firstVideoId }: FeedApiParams,
): Promise<Api_Feed> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channels/${encodeURIComponent(username!)}/timeline_feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get(...preventCache(url.href))

  return response.data
}

/**
 *
 * @param api
 * @param playlistId
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedPlaylistFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { playlistId, pageSize, firstVideoId }: FeedApiParams,
): Promise<Api_Feed> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/playlists/${encodeURIComponent(playlistId!)}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get(...preventCache(url.href))
  return response.data
}

/**
 *
 * @param api
 * @param username
 * @param hashtagExpression
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedUserTimelineHashtagFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { username, hashtagExpression, pageSize, firstVideoId }: FeedApiParams,
): Promise<Api_Feed> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channel_hashtags/${username}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  hashtagExpression &&
    url.searchParams.append('hashtag_filter_expression', hashtagExpression)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get(...preventCache(url.href))
  return response.data
}

/**
 *
 * @param api
 * @param username
 * @param pageSize
 * @param skus
 * @returns
 */
export async function createEmbedUserProductSKUFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { username, pageSize, skus, videoTypes }: FeedApiParams,
): Promise<Api_Feed> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channel_product_ext_ids/${username}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  skus && url.searchParams.append('skus', `${skus}`)

  if (videoTypes && videoTypes.length) {
    url.searchParams.append('video_types[]', videoTypes.join(','))
  }

  url.searchParams.append('n', apiCacheNonce)

  const response = await api.get(...preventCache(url.href))

  return response.data
}

/**
 *
 * @param api
 * @param id
 * @param pageSize
 * @param firstPlaylistId
 * @returns
 */
export async function createEmbedPlaylistCollectionFeed(
  api: AxiosInstance,
  id: string | undefined,
  pageSize?: number,
  firstPlaylistId?: string,
): Promise<Api_Feed> {
  const url = new URL(`${api.defaults.baseURL}/embed/v2/playlist_groups/${id}/`)
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstPlaylistId &&
    url.searchParams.append('first_playlist_id', firstPlaylistId)
  const response = await api.get(...preventCache(url.href))
  return response.data
}

/**
 * Creates a showroom schedule feed.
 * @param {AxiosInstance} api - The AxiosInstance used for making API requests.
 * @param {string} showroomId - The ID of the showroom for which the feed should be created.
 * @return {Promise<Api_Feed>} A promise that resolves to the created showroom schedule feed.
 */
export async function createShowroomScheduleFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  showroomId: string,
): Promise<Api_Feed> {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/showrooms/${showroomId}/feeds`,
  )
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get(...preventCache(url.href))
  return response.data
}

/**
 * Creates request for next page of given feed
 * @param api
 * @param feed
 * @param pageSize
 * @returns
 */
export async function getMoreFeedItems(
  api: AxiosInstance,
  feed: Api_Feed,
  pageSize?: number,
): Promise<Api_Feed> {
  const url = new URL(`${api.defaults.baseURL}${feed.next_page}`)
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  const response = await api.get(...preventCache(url.href))
  return response.data
}
